import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import Layout from '../../../../components/Layout';
import { useTill } from '../../../../context/TillContext';
import {
  tillSessionService,
  CASH_DENOMINATIONS,
  DenominationEntry,
  TillSessionDTO,
} from '../../../../services/tillSessionService';
import './TillPage.css';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtCurrency(amount: number): string {
  return amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function denomLabel(d: number): string {
  return d >= 1 ? d.toLocaleString() : d.toFixed(2);
}

function fmtDatetime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium', timeStyle: 'short',
  });
}

// ─── Component ────────────────────────────────────────────────────────────────

const TillClosePage: React.FC = () => {
  const { t }          = useTranslation();
  const navigate       = useNavigate();
  const { activeTill, clearTill } = useTill();

  const [counts, setCounts]   = useState<Record<number, number>>(
    Object.fromEntries(CASH_DENOMINATIONS.map(d => [d, 0]))
  );
  const [notes,   setNotes]   = useState('');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  const [closed,  setClosed]  = useState<TillSessionDTO | null>(null);

  // ─── Computed totals ──────────────────────────────────────────────────────

  const rows = useMemo<DenominationEntry[]>(
    () => CASH_DENOMINATIONS.map(d => ({
      denomination: d,
      count:        counts[d] ?? 0,
      subtotal:     d * (counts[d] ?? 0),
    })),
    [counts]
  );

  const declaredTotal = useMemo(() => rows.reduce((s, r) => s + r.subtotal, 0), [rows]);

  const variance = useMemo(() => {
    if (!closed) return null;
    return closed.variance ?? null;
  }, [closed]);

  const varianceClass = useMemo(() => {
    if (variance === null) return '';
    if (Math.abs(variance) <= 0.01) return 'till-variance--ok';
    return variance > 0 ? 'till-variance--over' : 'till-variance--short';
  }, [variance]);

  const varianceLabel = useMemo(() => {
    if (variance === null) return '';
    if (Math.abs(variance) <= 0.01) return t('till.varianceOk');
    return variance > 0
      ? t('till.varianceOver',  { amount: fmtCurrency(Math.abs(variance)) })
      : t('till.varianceShort', { amount: fmtCurrency(Math.abs(variance)) });
  }, [variance, t]);

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleCountChange = (denom: number, raw: string) => {
    const n = parseInt(raw, 10);
    setCounts(prev => ({ ...prev, [denom]: isNaN(n) || n < 0 ? 0 : n }));
  };

  const handleSubmit = async () => {
    setError(null);
    setLoading(true);
    try {
      const res     = await tillSessionService.closeSession({
        closingDenominations: rows,
        notes: notes.trim() || undefined,
      });
      const session = res.data?.data;
      if (session) {
        setClosed(session);
        clearTill();
      }
    } catch (err: any) {
      const code = err?.response?.data?.error?.code;
      if (code === 'TILL_NOT_OPEN') {
        setError(t('till.notOpen'));
      } else {
        setError(t('till.closeFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  // ─── Close result screen ──────────────────────────────────────────────────

  if (closed) {
    return (
      <Layout>
        <div className="ims-create-body-page till-page">
          <div className="ims-top-bar">
            <div className="ims-top-bar-left">
              <div>
                <h1 className="ims-page-title">{t('till.closeTitle')}</h1>
                <p className="ims-page-subtitle" style={{ color: 'var(--ims-green-text, #166534)' }}>
                  ✓ {t('till.closeSuccess', { number: closed.sessionNumber })}
                </p>
              </div>
            </div>
            <div className="ims-top-bar-right">
              <button className="ims-btn ims-btn-secondary" onClick={() => navigate('/sales/till/history')}>
                {t('till.tillHistory')}
              </button>
              <button className="ims-btn ims-btn-secondary" onClick={() => navigate('/sales/day-end')}>
                {t('dayEnd.title')}
              </button>
              <button className="ims-btn ims-btn-primary" onClick={() => navigate('/sales/sales-invoices')}>
                {t('salesInvoice.title')}
              </button>
            </div>
          </div>

          <div className="ims-create-body">
            <div className="ims-panel-left" style={{ flex: '0 0 480px', maxWidth: 480 }}>
              <div className="ims-card">
                <div className="ims-card-header">
                  <span className="ims-card-title">{t('till.closingDenominations')}</span>
                </div>
                <div className="ims-card-body" style={{ padding: '12px 0 0' }}>
                  <table className="till-denom-table">
                    <thead>
                      <tr>
                        <th>{t('till.denomination')}</th>
                        <th className="th-right">{t('till.count')}</th>
                        <th className="th-right">{t('till.subtotal')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(closed.closingDenominations ?? []).map(row => (
                        <tr key={row.denomination}>
                          <td>
                            <span className={`till-denom-label ${row.denomination >= 100 ? 'till-denom-label--note' : 'till-denom-label--coin'}`}>
                              {denomLabel(row.denomination)}
                            </span>
                          </td>
                          <td className="td-right">{row.count}</td>
                          <td className="td-right">
                            <span className="till-subtotal">
                              {row.subtotal > 0 ? fmtCurrency(row.subtotal) : '—'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="till-total-row">
                        <td colSpan={2}>{t('till.declaredClosingBalance')}</td>
                        <td className="td-right">{fmtCurrency(closed.declaredClosingBalance ?? 0)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>

            <div className="ims-panel-right">
              <div className="ims-card">
                <div className="ims-card-header">
                  <span className="ims-card-title">{t('till.reconciliation')}</span>
                </div>
                <div className="ims-card-body">
                  <div className="till-summary-card">
                    <div className="till-summary-row">
                      <span className="till-summary-label">{t('till.openingBalance')}</span>
                      <span className="till-summary-value">{fmtCurrency(closed.openingBalance)}</span>
                    </div>
                    <div className="till-summary-row">
                      <span className="till-summary-label">{t('till.expectedClosingBalance')}</span>
                      <span className="till-summary-value">{fmtCurrency(closed.expectedClosingBalance ?? 0)}</span>
                    </div>
                    <hr className="till-summary-divider" />
                    <div className="till-summary-row">
                      <span className="till-summary-label">{t('till.declaredClosingBalance')}</span>
                      <span className="till-summary-value">{fmtCurrency(closed.declaredClosingBalance ?? 0)}</span>
                    </div>
                    <div className="till-summary-row">
                      <span className="till-summary-label">{t('till.variance')}</span>
                      <span className={`till-summary-value ${varianceClass}`}>{varianceLabel}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // ─── Close form ───────────────────────────────────────────────────────────

  if (!activeTill) {
    return (
      <Layout>
        <div className="ims-create-body-page till-page">
          <div className="ims-top-bar">
            <div className="ims-top-bar-left">
              <button className="ims-back-btn" onClick={() => navigate(-1)}><ArrowLeft size={15} strokeWidth={2} /> {t('common.back')}</button>
              <h1 className="ims-page-title">{t('till.closeTitle')}</h1>
            </div>
          </div>
          <div className="ims-alert ims-alert-warning" style={{ margin: '24px' }}>
            {t('till.notOpen')}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="ims-create-body-page till-page">

        {/* Top bar */}
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button className="ims-back-btn" onClick={() => navigate(-1)}><ArrowLeft size={15} strokeWidth={2} /> {t('common.back')}</button>
            <div>
              <h1 className="ims-page-title">{t('till.closeTitle')}</h1>
              <p className="ims-page-subtitle">{t('till.closeSubtitle')}</p>
            </div>
          </div>
          <div className="ims-top-bar-right">
            <button className="btn-cancel" onClick={() => navigate(-1)} disabled={loading}>
              {t('common.cancel')}
            </button>
            <button className="btn-delete" onClick={handleSubmit} disabled={loading}>
              {loading ? t('common.saving') : t('till.closeTillButton')}
            </button>
          </div>
        </div>

        {error && (
          <div className="ims-error-banner">
            <div className="ims-alert ims-alert-error">{error}</div>
          </div>
        )}

        {/* Active session banner */}
        <div style={{ padding: '0 24px 0' }}>
          <div className="till-session-banner">
            <div>
              <div className="till-session-banner__number">{activeTill.sessionNumber}</div>
              <div className="till-session-banner__meta">
                {t('till.openedAt')}: {fmtDatetime(activeTill.openedAt)}
                &nbsp;·&nbsp;
                {t('till.cashier')}: {activeTill.cashierName}
              </div>
            </div>
            <div className="till-session-banner__badge">
              <span className="ims-badge ims-badge-approved">{t('till.statusOpen')}</span>
            </div>
          </div>
        </div>

        <div className="ims-create-body">

          {/* Left panel — closing denomination table */}
          <div className="ims-panel-left" style={{ flex: '0 0 460px', maxWidth: 460 }}>
            <div className="ims-card">
              <div className="ims-card-header">
                <span className="ims-card-title">{t('till.closingDenominations')}</span>
              </div>
              <div className="ims-card-body" style={{ padding: '12px 0 0' }}>
                <table className="till-denom-table">
                  <thead>
                    <tr>
                      <th>{t('till.denomination')}</th>
                      <th className="th-right">{t('till.count')}</th>
                      <th className="th-right">{t('till.subtotal')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, idx) => (
                      <tr key={row.denomination}>
                        <td>
                          <span className={`till-denom-label ${row.denomination >= 100 ? 'till-denom-label--note' : 'till-denom-label--coin'}`}>
                            {denomLabel(row.denomination)}
                          </span>
                        </td>
                        <td className="td-right">
                          <input
                            className="till-count-input"
                            type="number"
                            min={0}
                            value={counts[row.denomination] || ''}
                            placeholder="0"
                            onChange={e => handleCountChange(row.denomination, e.target.value)}
                          />
                        </td>
                        <td className="td-right">
                          <span className="till-subtotal">
                            {row.subtotal > 0 ? fmtCurrency(row.subtotal) : '—'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="till-total-row">
                      <td colSpan={2}>{t('till.declaredClosingBalance')}</td>
                      <td className="td-right">{fmtCurrency(declaredTotal)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>

          {/* Right panel — opening summary + notes */}
          <div className="ims-panel-right">
            <div className="ims-card">
              <div className="ims-card-header">
                <span className="ims-card-title">{t('till.sessionInfo')}</span>
              </div>
              <div className="ims-card-body">
                <div className="till-summary-card">
                  <div className="till-summary-row">
                    <span className="till-summary-label">{t('till.openingBalance')}</span>
                    <span className="till-summary-value">{fmtCurrency(activeTill.openingBalance)}</span>
                  </div>
                  <div className="till-summary-row">
                    <span className="till-summary-label">{t('till.declaredClosingBalance')}</span>
                    <span className="till-summary-value till-total-value">{fmtCurrency(declaredTotal)}</span>
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <p className="ims-text-muted" style={{ fontSize: 11 }}>
                      {t('till.expectedBalanceNote')}
                    </p>
                  </div>
                </div>

                <div className="ims-form-group" style={{ marginTop: 8 }}>
                  <label className="ims-form-label">{t('till.notes')}</label>
                  <textarea
                    className="ims-form-control"
                    rows={3}
                    value={notes}
                    placeholder={t('till.notesPlaceholder')}
                    onChange={e => setNotes(e.target.value)}
                    style={{ resize: 'vertical' }}
                  />
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </Layout>
  );
};

export default TillClosePage;
